import { useState, useEffect, useCallback, Fragment } from "react";
import { C, S } from "../customers/theme";
import { GetStartedCTA } from "../components/DemoDataControls";
import {
  getInvoices, getInvoice, getInvoiceStats, createInvoice, updateInvoice, deleteInvoice,
  duplicateInvoice, markSent, cancelInvoice, newInvoiceDraft,
  INV_STATUSES, OVERDUE_STATUS, displayStatus,
} from "../lib/invoices";
import { recordPayment, listPayments, voidPayment, PAYMENT_METHODS } from "../lib/payments";
import { listCustomers } from "../lib/customers";
import { todayISO } from "../lib/dates";
import { getProducts } from "../lib/products";
import { downloadInvoice } from "./invoicePdfAdapter";
import { emailInvoice, emailInvoiceReminder } from "../lib/emailLinks";
import InvoiceForm from "./InvoiceForm";
import InvoiceView, { INVStatusBadge } from "./InvoiceView";
import CreditNoteForm from "../creditnotes/CreditNoteForm";
import { getCreditNotesForInvoice } from "../lib/creditNotes";

const PAGE_SIZE = 25;
const money = (n, cur = "€") => `${cur}${(Number(n) || 0).toFixed(2)}`;
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const monthLabel = (iso) => { if (!iso) return "No date"; const [y, m] = iso.slice(0, 7).split("-"); return `${MONTHS[+m - 1] || "?"} ${y}`; };

function Th({ label, col, sortBy, sortDir, onSort }) {
  const active = sortBy === col;
  return (
    <th style={S.th} aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}>
      <button onClick={() => onSort(col)} style={{ background: "none", border: "none", color: "inherit", font: "inherit", cursor: "pointer", padding: 0, display: "inline-flex", alignItems: "center", gap: 4, textTransform: "inherit", letterSpacing: "inherit" }}>
        {label}<span style={{ opacity: active ? 1 : 0.25, fontSize: 10 }}>{active ? (sortDir === "asc" ? "▲" : "▼") : "↕"}</span>
      </button>
    </th>
  );
}

function Kpi({ label, value, sub, color }) {
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderLeft: `3px solid ${color}`, borderRadius: 10, padding: "12px 16px", minWidth: 150, flex: "1 1 150px" }}>
      <div style={{ color: C.textMuted, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, marginTop: 2 }}>{value}</div>
      {sub && <div style={{ color: C.textMuted, fontSize: 12, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function PaymentModal({ invoice, onClose, onConfirm }) {
  const outstanding = Number(invoice.amount_outstanding) || 0;
  const [amount, setAmount] = useState(outstanding > 0 ? outstanding : "");
  const [date, setDate] = useState(todayISO());
  const [method, setMethod] = useState("bank_transfer");
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const cur = invoice.preferred_currency === "GBP" ? "£" : invoice.preferred_currency === "USD" ? "$" : "€";
  const over = Number(amount) > outstanding + 0.005;
  const submit = async () => {
    setBusy(true); setError("");
    try { await onConfirm({ amount: Number(amount), payment_date: date, method, reference }); }
    catch (e) { setError(e.message || "Could not record payment"); setBusy(false); }
  };
  return (
    <div style={S.modal} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{ ...S.modalBox, width: 440 }}>
        <h2 style={{ margin: "0 0 6px", fontSize: 17, fontWeight: 700 }}>Record payment</h2>
        <div style={{ color: C.textMuted, fontSize: 13, marginBottom: 14 }}>{invoice.invoice_number} · outstanding {money(outstanding, cur)}</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <label htmlFor="pay-amount" style={S.label}>Amount received</label>
            <input id="pay-amount" style={S.input} type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
          </div>
          <div>
            <label htmlFor="pay-date" style={S.label}>Date</label>
            <input id="pay-date" style={S.input} type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div>
            <label htmlFor="pay-method" style={S.label}>Method</label>
            <select id="pay-method" style={S.input} value={method} onChange={(e) => setMethod(e.target.value)}>
              {PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="pay-ref" style={S.label}>Reference</label>
            <input id="pay-ref" style={S.input} value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Optional" />
          </div>
        </div>
        {over && <div style={{ color: C.warning, fontSize: 12.5, fontWeight: 600, marginTop: 10 }}>This exceeds the outstanding balance ({money(outstanding, cur)}) — the invoice will show as overpaid.</div>}
        {error && <div style={{ color: C.danger, fontSize: 13, fontWeight: 600, marginTop: 10 }} role="alert">{error}</div>}
        <div style={{ display: "flex", gap: 10, marginTop: 18, justifyContent: "flex-end" }}>
          <button style={S.btn("secondary")} onClick={onClose} disabled={busy}>Cancel</button>
          <button style={{ ...S.btn(), opacity: busy ? 0.6 : 1 }} onClick={submit} disabled={busy}>{busy ? "Saving…" : "Record payment"}</button>
        </div>
      </div>
    </div>
  );
}

export default function InvoiceManager({ autoNew, onAutoNew, openId, onOpenHandled, onOpenCreditNote }) {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [customerId, setCustomerId] = useState("all");
  const [sortBy, setSortBy] = useState("issue_date");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [paying, setPaying] = useState(null);
  const [viewPayments, setViewPayments] = useState([]);
  const [viewCredits, setViewCredits] = useState([]);
  const [creditingInvoice, setCreditingInvoice] = useState(null);
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [{ data, count: total }, s] = await Promise.all([
        getInvoices({ search, status, customerId, page, pageSize: PAGE_SIZE, sortBy, sortDir }),
        getInvoiceStats(),
      ]);
      setRows(data); setCount(total); setStats(s);
    } catch (e) { setError(e.message || "Could not load invoices."); }
    finally { setLoading(false); }
  }, [search, status, customerId, sortBy, sortDir, page]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const loadRefs = useCallback(async () => {
    try { setCustomers(await listCustomers({ status: "active" })); } catch { /* filter degrades */ }
    try { const { data } = await getProducts({ pageSize: 1000, sortBy: "name" }); setProducts(data); } catch { /* picker degrades */ }
  }, []);
  useEffect(() => { loadRefs(); }, [loadRefs]);

  const openNew = useCallback(async () => {
    try { setEditing(await newInvoiceDraft()); } catch (e) { setError(e.message); }
  }, []);

  useEffect(() => {
    if (autoNew) { openNew(); onAutoNew && onAutoNew(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoNew]);

  useEffect(() => {
    if (!openId) return;
    (async () => {
      try {
        const inv = await getInvoice(openId);
        if (inv) { if (inv.status === "draft") setEditing(inv); else { setViewing(inv); setViewPayments(await listPayments(openId)); setViewCredits(await getCreditNotesForInvoice(openId)); } }
      } catch (e) { setError(e.message); } finally { onOpenHandled && onOpenHandled(); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId]);

  const onSearch = (v) => { setSearch(v); setPage(1); };
  const onStatus = (v) => { setStatus(v); setPage(1); };
  const onCustomer = (v) => { setCustomerId(v); setPage(1); };
  const onSort = (col) => { setPage(1); if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc")); else { setSortBy(col); setSortDir("asc"); } };
  const clearFilters = () => { setSearch(""); setStatus("all"); setCustomerId("all"); setPage(1); };

  const openEdit = async (inv) => { try { setViewing(null); setEditing(await getInvoice(inv.id)); } catch (e) { setError(e.message); } };
  const openViewById = useCallback(async (id) => {
    const [inv, pays, credits] = await Promise.all([getInvoice(id), listPayments(id), getCreditNotesForInvoice(id)]);
    setViewing(inv); setViewPayments(pays); setViewCredits(credits);
  }, []);
  const openView = async (inv) => { try { await openViewById(inv.id); } catch (e) { setError(e.message); } };

  const save = async (payload) => {
    if (editing && editing.id) await updateInvoice(editing.id, payload);
    else await createInvoice(payload);
    setEditing(null);
    await load();
  };
  const del = async (inv) => {
    if (!window.confirm(`Delete ${inv.invoice_number}? This cannot be undone.`)) return;
    try { await deleteInvoice(inv.id); await load(); } catch (e) { setError(e.message); }
  };
  const act = async (fn) => { setActing(true); setError(""); try { await fn(); await load(); } catch (e) { setError(e.message); } finally { setActing(false); } };
  const doMarkSent = (inv) => act(async () => { await markSent(inv.id); await openViewById(inv.id); });
  const doCancel = (inv) => { if (!window.confirm(`Cancel ${inv.invoice_number}?`)) return; act(async () => { await cancelInvoice(inv.id); setViewing(null); }); };
  const doDuplicate = (inv) => act(async () => { const id = await duplicateInvoice(inv.id); setViewing(null); setEditing(await getInvoice(id)); });
  const confirmPayment = async (payload) => { await recordPayment(paying.id, payload); const id = paying.id; setPaying(null); await openViewById(id); await load(); };
  const doVoidPayment = async (paymentId) => {
    if (!window.confirm("Void this payment? It stays in the history for audit.")) return;
    try { await voidPayment(paymentId); if (viewing) await openViewById(viewing.id); await load(); } catch (e) { setError(e.message); }
  };
  const doDownload = async (inv) => { try { downloadInvoice(await getInvoice(inv.id) || inv); } catch (e) { setError(e.message); } };

  // SEND-1 — one click: download the PDF, then open the prefilled mail. The PDF
  // goes first so it is already on disk when the mail window appears and the
  // prompt tells the user to attach it.
  //
  // ⛔ The variant is chosen from the DERIVED status, not invoice.status: overdue
  // is never stored, so reading the raw status would send "Email again" to every
  // customer who is late.
  const doEmail = (inv) => {
    try {
      downloadInvoice(inv);
      if (displayStatus(inv) === "overdue") emailInvoiceReminder(inv);
      else emailInvoice(inv);
    } catch (e) { setError(e.message || "Could not open your email app."); }
  };

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const from = count === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, count);
  const hasFilters = !!search || status !== "all" || customerId !== "all";
  const selStyle = { ...S.input, width: "auto", minWidth: 150 };
  const grouped = sortBy === "issue_date";

  const statusOptions = [...INV_STATUSES, OVERDUE_STATUS];

  return (
    <div>
      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <Kpi label="Outstanding" value={money(stats?.outstanding)} sub="sent + part-paid" color={C.warning} />
        <Kpi label="Overdue" value={stats?.overdueCount ?? 0} sub={money(stats?.overdueAmount)} color={C.danger} />
        <Kpi label="Paid this month" value={money(stats?.paidThisMonth)} sub="issued this month" color={C.success} />
        <Kpi label="Drafts" value={stats?.draftCount ?? 0} sub="not yet sent" color={C.textDim} />
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <input style={S.searchBar} placeholder="Search invoice no., customer, SO…" value={search} onChange={(e) => onSearch(e.target.value)} aria-label="Search invoices" />
        <select style={selStyle} value={customerId} onChange={(e) => onCustomer(e.target.value)} aria-label="Filter by customer">
          <option value="all">All customers</option>
          {customers.map((cu) => <option key={cu.id} value={cu.id}>{cu.company_name}</option>)}
        </select>
        <select style={selStyle} value={status} onChange={(e) => onStatus(e.target.value)} aria-label="Filter by status">
          <option value="all">All statuses</option>
          {statusOptions.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <div style={{ flex: 1 }} />
        <span style={{ color: C.textMuted, fontSize: 12 }}>{count} invoice{count === 1 ? "" : "s"}</span>
        <button style={S.btn()} onClick={openNew}>+ New Invoice</button>
      </div>

      {error && (
        <div role="alert" style={{ display: "flex", alignItems: "center", gap: 12, background: C.dangerDim, border: `1px solid ${C.danger}`, color: C.text, borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>
          <span style={{ color: C.danger, fontWeight: 700 }}>⚠ {error}</span>
          <button style={S.btn("secondary")} onClick={() => setError("")}>Dismiss</button>
        </div>
      )}

      <div style={S.card}>
        <table style={S.table}>
          <thead>
            <tr>
              <Th label="Invoice No." col="number" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <th style={S.th}>Customer</th>
              <Th label="Issue Date" col="issue_date" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <Th label="Due" col="due_date" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <Th label="Status" col="status" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <Th label="Total" col="total" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <Th label="Outstanding" col="outstanding" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <th style={S.th} aria-label="Actions"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={S.empty}>Loading…</td></tr>
            ) : count === 0 ? (
              <tr><td colSpan={8} style={S.empty}>
                {hasFilters ? (
                  <div>No invoices match your filters.<div style={{ marginTop: 10 }}><button style={S.btn("secondary")} onClick={clearFilters}>Clear filters</button></div></div>
                ) : (
                  <GetStartedCTA onLoaded={load} fallback={
                    <div>
                      <div style={{ fontWeight: 600, color: C.text, marginBottom: 4 }}>No invoices yet</div>
                      <div style={{ marginBottom: 12 }}>Create one from a completed Sales Order, or start a manual invoice here.</div>
                      <button style={S.btn()} onClick={openNew}>+ New Invoice</button>
                    </div>
                  } />
                )}
              </td></tr>
            ) : rows.map((inv, i) => {
              const cur = inv.preferred_currency === "GBP" ? "£" : inv.preferred_currency === "USD" ? "$" : "€";
              const showMonth = grouped && (i === 0 || monthLabel(inv.issue_date) !== monthLabel(rows[i - 1].issue_date));
              const isDraft = inv.status === "draft";
              return (
                <Fragment key={inv.id}>
                  {showMonth && (
                    <tr>
                      <td colSpan={8} style={{ padding: "10px 12px 6px", color: C.textMuted, fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em", background: C.surface }}>{monthLabel(inv.issue_date)}</td>
                    </tr>
                  )}
                  <tr style={{ cursor: "pointer" }} onClick={() => openView(inv)}>
                    <td style={S.td}><span style={{ color: C.textMuted, fontFamily: "monospace", fontSize: 12 }}>{inv.invoice_number}</span></td>
                    <td style={S.td}>{inv.customer_name || inv.customers?.company_name || <span style={{ color: C.textDim }}>—</span>}</td>
                    <td style={S.td}>{inv.issue_date || "—"}</td>
                    <td style={S.td}><span style={{ color: displayStatus(inv) === "overdue" ? C.danger : C.text }}>{inv.due_date || "—"}</span></td>
                    <td style={S.td}><INVStatusBadge invoice={inv} /></td>
                    <td style={S.td}>{money(inv.total_amount, cur)}</td>
                    <td style={S.td}>{Number(inv.amount_outstanding) < 0
                      ? <span style={{ color: C.success }}>{money(Math.abs(inv.amount_outstanding), cur)} CR</span>
                      : money(inv.amount_outstanding, cur)}</td>
                    <td style={S.td} onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button style={S.btn("ghost")} title="View" aria-label={`View ${inv.invoice_number}`} onClick={() => openView(inv)}>👁</button>
                        {isDraft && <button style={S.btn("ghost")} title="Edit" aria-label={`Edit ${inv.invoice_number}`} onClick={() => openEdit(inv)}>✎</button>}
                        <button style={S.btn("ghost")} title="Download PDF" aria-label={`Download ${inv.invoice_number}`} onClick={() => doDownload(inv)}>⬇</button>
                        {isDraft && <button style={S.btn("ghost")} title="Delete" aria-label={`Delete ${inv.invoice_number}`} onClick={() => del(inv)}>🗑</button>}
                      </div>
                    </td>
                  </tr>
                </Fragment>
              );
            })}
          </tbody>
        </table>

        {count > 0 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 14, flexWrap: "wrap", gap: 10 }}>
            <span style={{ color: C.textMuted, fontSize: 12 }}>Showing {from}–{to} of {count}</span>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button style={{ ...S.btn("secondary"), opacity: page <= 1 ? 0.5 : 1 }} disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} aria-label="Previous page">← Prev</button>
              <span style={{ color: C.textMuted, fontSize: 12 }}>Page {page} of {totalPages}</span>
              <button style={{ ...S.btn("secondary"), opacity: page >= totalPages ? 0.5 : 1 }} disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} aria-label="Next page">Next →</button>
            </div>
          </div>
        )}
      </div>

      {editing !== null && (
        <InvoiceForm initial={editing} customers={customers} products={products} onCancel={() => setEditing(null)} onSave={save} />
      )}
      {viewing && (
        <InvoiceView invoice={viewing} payments={viewPayments} creditNotes={viewCredits} busy={acting}
          onClose={() => { setViewing(null); setViewPayments([]); setViewCredits([]); }}
          onEdit={openEdit} onMarkSent={doMarkSent} onRecordPayment={(inv) => setPaying(inv)} onVoidPayment={doVoidPayment}
          onCreateCreditNote={(inv) => setCreditingInvoice(inv)} onOpenCreditNote={onOpenCreditNote}
          onCancel={doCancel} onDuplicate={doDuplicate} onDownload={downloadInvoice} onEmail={doEmail} />
      )}
      {paying && <PaymentModal invoice={paying} onClose={() => setPaying(null)} onConfirm={confirmPayment} />}
      {creditingInvoice && (
        <CreditNoteForm invoice={creditingInvoice} existingCredits={viewCredits}
          onClose={() => setCreditingInvoice(null)}
          onSaved={async () => { setCreditingInvoice(null); if (viewing) await openViewById(viewing.id); }} />
      )}
    </div>
  );
}
